import React, { useEffect, useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteAccount } from '../../api/auth';
import { User, Cpu, Shield, Bell, Clock, FileText, Heart, HelpCircle, ExternalLink, LogOut, Trash2, ChevronRight, Pencil, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../store/authStore';
import { getMyDevices } from '../../api/devices';
import { ToggleSwitch } from '../../components/ToggleSwitch';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { EditProfile } from './EditProfile';
import { DeviceManagement } from './DeviceManagement';
type Section = 'personal' | 'devices' | 'security' | 'notifications' | 'about' | 'help';
export function ProfileScreen() {
  const navigate = useNavigate();
  const {
    user,
    token,
    logout
  } = useAuth();
  const [deviceCount, setDeviceCount] = useState<number | null>(null);
  const [isLoadingDevices, setIsLoadingDevices] = useState(true);
  // Desktop master-detail state
  const [activeSection, setActiveSection] = useState<Section>('personal');
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  // Bottom sheet states
  const [showSecuritySheet, setShowSecuritySheet] = useState(false);
  const [showAboutSheet, setShowAboutSheet] = useState(false);
  const [showHelpSheet, setShowHelpSheet] = useState(false);
  const [showLogoutSheet, setShowLogoutSheet] = useState(false);
  const [showDeleteSheet, setShowDeleteSheet] = useState(false);
  // Delete account state
  const [deleteInput, setDeleteInput] = useState('');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  useEffect(() => {
    if (token) {
      setIsLoadingDevices(true);
      getMyDevices(token).then(devices => setDeviceCount(devices.length)).catch(err => {}).finally(() => setIsLoadingDevices(false));
    }
  }, [token]);
  const getInitials = (name: string) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };
  const getMemberSince = (dateString: string) => {
    if (!dateString) return 'Member since recently';
    const date = new Date(dateString);
    return `Member since ${date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    })}`;
  };
  const handleNavigation = (path: string, section: Section) => {
    if (isDesktop) {
      setActiveSection(section);
    } else {
      navigate(path);
    }
  };
  const handleLogout = () => {
    setIsLoggingOut(true);
    setTimeout(() => {
      logout();
      navigate('/splash');
    }, 800);
  };
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const handleDeleteAccount = async () => {
    if (!token) return;
    setIsLoggingOut(true);
    setDeleteError(null);
    try {
      await deleteAccount(token);
      logout();
      navigate('/intro');
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete account');
      setIsLoggingOut(false);
    }
  };
  const renderMenuContent = () => <div className="space-y-6 pb-20 md:pb-0" data-id="element-1907">
      {/* User Card */}
      <div className="bg-white rounded-[24px] p-6 shadow-card relative" data-id="element-1908">
        <button onClick={() => handleNavigation('/app/profile/edit', 'personal')} className="absolute top-6 right-6 w-[36px] h-[36px] rounded-full bg-[#E9D5FF] flex items-center justify-center hover:bg-[#E5E2DD] transition-colors" aria-label="Edit Profile" data-id="element-1909">
          <Pencil size={16} className="text-text-secondary" data-id="element-1910" />
        </button>

        <div className="flex flex-col items-center text-center" data-id="element-1911">
          <div className="w-[80px] h-[80px] rounded-full bg-primary flex items-center justify-center text-white text-[28px] font-bold mb-4" data-id="element-1912">
            {getInitials(user?.fullName)}
          </div>
          <h2 className="text-[22px] font-semibold text-text-primary mb-1" data-id="element-1913">
            {user?.fullName || 'User'}
          </h2>
          <p className="text-[15px] text-text-secondary mb-3" data-id="element-1914">
            {user?.email || 'user@example.com'}
          </p>
          <div className="bg-primary/20 text-primary text-[13px] font-medium px-3 py-1 rounded-pill mb-2" data-id="element-1915">
            Active Account
          </div>
          <p className="text-[13px] text-text-secondary" data-id="element-1916">
            {getMemberSince(user?.createdAt)}
          </p>
        </div>
      </div>

      {/* Account Section */}
      <div data-id="element-1917">
        <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] mb-3 px-2" data-id="element-1918">
          Account
        </h3>
        <div className="bg-white rounded-[20px] shadow-card overflow-hidden" data-id="element-1919">
          <MenuRow icon={<User size={20} className="text-primary" data-id="element-1921" />} iconBg="bg-primary/15" title="Personal Information" subtitle="Name, email, phone" onClick={() => handleNavigation('/app/profile/edit', 'personal')} isActive={isDesktop && activeSection === 'personal'} data-id="element-1920" />
          <div className="h-[1px] w-full bg-border" data-id="element-1922" />
          <MenuRow icon={<Cpu size={20} className="text-primary" data-id="element-1924" />} iconBg="bg-primary/15" title="Connected Devices" subtitle={isLoadingDevices ? <SkeletonBlock width="100px" height="16px" className="mt-1" data-id="element-1925" /> : deviceCount === 0 ? 'No devices' : `${deviceCount} device${deviceCount === 1 ? '' : 's'} connected`} onClick={() => handleNavigation('/app/profile/devices', 'devices')} isActive={isDesktop && activeSection === 'devices'} data-id="element-1923" />
          <div className="h-[1px] w-full bg-border" data-id="element-1926" />
          <MenuRow icon={<Shield size={20} className="text-success" data-id="element-1928" />} iconBg="bg-success/15" title="Security" subtitle="Password and account security" onClick={() => {
          if (isDesktop) setActiveSection('security');else setShowSecuritySheet(true);
        }} isActive={isDesktop && activeSection === 'security'} data-id="element-1927" />
        </div>
      </div>

      {/* Notifications Section */}
      <div data-id="element-1929">
        <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] mb-3 px-2" data-id="element-1930">
          Notifications
        </h3>
        <div className="bg-white rounded-[20px] shadow-card overflow-hidden" data-id="element-1931">
          <MenuRow icon={<Bell size={20} className="text-primary" data-id="element-1933" />} iconBg="bg-primary/15" title="Alert Notifications" subtitle="Get notified when alerts are triggered" rightElement={<ToggleSwitch storageKey="aurora_alert_notifications" defaultChecked={true} label="Alert Notifications" data-id="element-1934" />} data-id="element-1932" />
          <div className="h-[1px] w-full bg-border" data-id="element-1935" />
          <MenuRow icon={<Clock size={20} className="text-[#F59E0B]" data-id="element-1937" />} iconBg="bg-[#F59E0B]/15" title="Session Reminders" subtitle="Daily reminder to monitor" rightElement={<ToggleSwitch storageKey="aurora_session_reminders" defaultChecked={false} label="Session Reminders" data-id="element-1938" />} data-id="element-1936" />
          <div className="h-[1px] w-full bg-border" data-id="element-1939" />
          <MenuRow icon={<FileText size={20} className="text-success" data-id="element-1941" />} iconBg="bg-success/15" title="Report Notifications" subtitle="Notify when report is delivered" rightElement={<ToggleSwitch storageKey="aurora_report_notifications" defaultChecked={true} label="Report Notifications" data-id="element-1942" />} data-id="element-1940" />
        </div>
      </div>

      {/* App Section */}
      <div data-id="element-1943">
        <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] mb-3 px-2" data-id="element-1944">
          App
        </h3>
        <div className="bg-white rounded-[20px] shadow-card overflow-hidden" data-id="element-1945">
          <MenuRow icon={<Heart size={20} className="text-primary" data-id="element-1947" />} iconBg="bg-primary/15" title="About Aurora Care" subtitle="Version 1.0.0" onClick={() => {
          if (isDesktop) setActiveSection('about');else setShowAboutSheet(true);
        }} isActive={isDesktop && activeSection === 'about'} data-id="element-1946" />
          <div className="h-[1px] w-full bg-border" data-id="element-1948" />
          <MenuRow icon={<HelpCircle size={20} className="text-success" data-id="element-1950" />} iconBg="bg-success/15" title="Help & Support" subtitle="FAQs and contact support" onClick={() => {
          if (isDesktop) setActiveSection('help');else setShowHelpSheet(true);
        }} isActive={isDesktop && activeSection === 'help'} data-id="element-1949" />
          <div className="h-[1px] w-full bg-border" data-id="element-1951" />
          <MenuRow icon={<FileText size={20} className="text-success" data-id="element-1953" />} iconBg="bg-success/15" title="Privacy Policy" rightElement={<ExternalLink size={18} className="text-text-secondary" data-id="element-1954" />} onClick={() => window.open('https://example.com/privacy', '_blank')} data-id="element-1952" />
        </div>
      </div>

      {/* Danger Zone */}
      <div data-id="element-1955">
        <h3 className="text-[12px] uppercase font-semibold text-danger tracking-[1px] mb-3 px-2" data-id="element-1956">
          Account Actions
        </h3>
        <div className="bg-white rounded-[20px] shadow-card overflow-hidden" data-id="element-1957">
          <MenuRow icon={<LogOut size={20} className="text-danger" data-id="element-1959" />} iconBg="bg-danger/15" title={<span className="text-danger" data-id="element-1960">Sign Out</span>} onClick={() => setShowLogoutSheet(true)} hideChevron data-id="element-1958" />
          <div className="h-[1px] w-full bg-border" data-id="element-1961" />
          <MenuRow icon={<Trash2 size={20} className="text-danger" data-id="element-1963" />} iconBg="bg-danger/15" title={<span className="text-danger" data-id="element-1964">Delete Account</span>} subtitle={<span className="text-danger/70" data-id="element-1965">
                Permanently delete your account and data
              </span>} onClick={() => setShowDeleteSheet(true)} hideChevron data-id="element-1962" />
        </div>
      </div>
    </div>;
  const renderRightPanel = () => {
    switch (activeSection) {
      case 'personal':
        return <EditProfile isPanel data-id="element-1966" />;
      case 'devices':
        return <DeviceManagement isPanel data-id="element-1967" />;
      case 'security':
        return <div className="bg-white rounded-[24px] p-8 shadow-card h-full flex flex-col items-center justify-center text-center" data-id="element-1968">
            <Shield size={48} className="text-success mb-4" data-id="element-1969" />
            <h2 className="text-[20px] font-semibold text-text-primary mb-2" data-id="element-1970">
              Security Settings
            </h2>
            <p className="text-[15px] text-text-secondary max-w-[300px]" data-id="element-1971">
              Password change via email link — coming soon.
            </p>
          </div>;
      case 'about':
        return <div className="bg-white rounded-[24px] p-8 shadow-card h-full flex flex-col items-center justify-center text-center" data-id="element-1972">
            <div className="w-[80px] h-[80px] bg-primary rounded-full flex items-center justify-center mb-4" data-id="element-1973">
              <Heart size={36} className="text-white fill-white" data-id="element-1974" />
            </div>
            <h2 className="text-[28px] font-semibold text-text-primary mb-1" data-id="element-1975">
              Aurora Care
            </h2>
            <p className="text-[15px] text-text-secondary mb-8" data-id="element-1976">
              Version 1.0.0
            </p>

            <div className="w-full max-w-[300px] h-[1px] bg-border mb-6" data-id="element-1977" />

            <p className="text-[14px] text-text-secondary mb-3" data-id="element-1978">Built with</p>
            <div className="flex gap-2 mb-8" data-id="element-1979">
              <span className="bg-secondary px-3 py-1 rounded-pill text-[12px] font-medium text-text-primary" data-id="element-1980">
                React
              </span>
              <span className="bg-secondary px-3 py-1 rounded-pill text-[12px] font-medium text-text-primary" data-id="element-1981">
                TypeScript
              </span>
              <span className="bg-secondary px-3 py-1 rounded-pill text-[12px] font-medium text-text-primary" data-id="element-1982">
                WebSockets
              </span>
            </div>

            <div className="w-full max-w-[300px] h-[1px] bg-border mb-6" data-id="element-1983" />

            <p className="text-[13px] text-text-secondary" data-id="element-1984">
              © 2026 Aurora Care
            </p>
          </div>;
      case 'help':
        return <div className="bg-white rounded-[24px] p-8 shadow-card h-full flex flex-col items-center justify-center text-center" data-id="element-1985">
            <HelpCircle size={48} className="text-success mb-4" data-id="element-1986" />
            <h2 className="text-[20px] font-semibold text-text-primary mb-2" data-id="element-1987">
              Help & Support
            </h2>
            <p className="text-[15px] text-text-secondary max-w-[300px] mb-6" data-id="element-1988">
              Our support team is here to help you with any questions or issues.
            </p>
            <button className="bg-primary text-white px-6 py-3 rounded-[14px] font-medium" data-id="element-1989">
              Contact Support
            </button>
          </div>;
      default:
        return null;
    }
  };
  return <main className="w-full max-w-[1200px] mx-auto animate-fadeInUp" data-id="element-1990">
      {isDesktop ? <div className="flex gap-8 h-[calc(100vh-120px)]" data-id="element-1991">
          <div className="w-[340px] flex-shrink-0 overflow-y-auto pr-2 custom-scrollbar" data-id="element-1992">
            {renderMenuContent()}
          </div>
          <div className="flex-1 overflow-y-auto pb-8" data-id="element-1993">
            {renderRightPanel()}
          </div>
        </div> : <div className="max-w-[600px] mx-auto" data-id="element-1994">{renderMenuContent()}</div>}

      {/* Security Bottom Sheet (Mobile) */}
      <BottomSheet isOpen={showSecuritySheet} onClose={() => setShowSecuritySheet(false)} data-id="element-1995">
        <div className="text-center py-4" data-id="element-1996">
          <Shield size={40} className="text-success mx-auto mb-4" data-id="element-1997" />
          <h2 className="text-[18px] font-semibold text-text-primary mb-2" data-id="element-1998">
            Security
          </h2>
          <p className="text-[15px] text-text-secondary mb-6" data-id="element-1999">
            Password change via email link — coming soon.
          </p>
          <button onClick={() => setShowSecuritySheet(false)} className="w-full py-3 text-text-secondary font-medium" data-id="element-2000">
            Close
          </button>
        </div>
      </BottomSheet>

      {/* About Bottom Sheet (Mobile) */}
      <BottomSheet isOpen={showAboutSheet} onClose={() => setShowAboutSheet(false)} data-id="element-2001">
        <div className="text-center py-4" data-id="element-2002">
          <div className="w-[60px] h-[60px] bg-primary rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-2003">
            <Heart size={28} className="text-white fill-white" data-id="element-2004" />
          </div>
          <h2 className="text-[24px] font-semibold text-text-primary mb-1" data-id="element-2005">
            Aurora Care
          </h2>
          <p className="text-[14px] text-text-secondary mb-6" data-id="element-2006">Version 1.0.0</p>

          <div className="w-full h-[1px] bg-border mb-6" data-id="element-2007" />

          <div className="flex flex-col items-center mb-6" data-id="element-2008">
            <p className="text-[14px] text-text-secondary mb-3" data-id="element-2009">Built with</p>
            <div className="flex gap-2" data-id="element-2010">
              <span className="bg-secondary px-3 py-1 rounded-pill text-[12px] font-medium text-text-primary" data-id="element-2011">
                React
              </span>
              <span className="bg-secondary px-3 py-1 rounded-pill text-[12px] font-medium text-text-primary" data-id="element-2012">
                TypeScript
              </span>
              <span className="bg-secondary px-3 py-1 rounded-pill text-[12px] font-medium text-text-primary" data-id="element-2013">
                WebSockets
              </span>
            </div>
          </div>

          <div className="w-full h-[1px] bg-border mb-6" data-id="element-2014" />

          <p className="text-[13px] text-text-secondary mb-6" data-id="element-2015">
            © 2026 Aurora Care
          </p>

          <button onClick={() => setShowAboutSheet(false)} className="w-full py-3 text-text-secondary font-medium" data-id="element-2016">
            Close
          </button>
        </div>
      </BottomSheet>

      {/* Help Bottom Sheet (Mobile) */}
      <BottomSheet isOpen={showHelpSheet} onClose={() => setShowHelpSheet(false)} data-id="element-2017">
        <div className="text-center py-4" data-id="element-2018">
          <HelpCircle size={40} className="text-success mx-auto mb-4" data-id="element-2019" />
          <h2 className="text-[18px] font-semibold text-text-primary mb-2" data-id="element-2020">
            Help & Support
          </h2>
          <p className="text-[15px] text-text-secondary mb-6" data-id="element-2021">
            Our support team is here to help you with any questions or issues.
          </p>
          <button className="w-full h-[52px] bg-primary text-white rounded-[14px] font-medium mb-3" data-id="element-2022">
            Contact Support
          </button>
          <button onClick={() => setShowHelpSheet(false)} className="w-full py-3 text-text-secondary font-medium" data-id="element-2023">
            Close
          </button>
        </div>
      </BottomSheet>

      {/* Logout Bottom Sheet */}
      <BottomSheet isOpen={showLogoutSheet} onClose={() => !isLoggingOut && setShowLogoutSheet(false)} data-id="element-2024">
        <div className="text-center py-2" data-id="element-2025">
          <div className="w-[48px] h-[48px] bg-primary rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2026">
            <Heart size={24} className="text-white fill-white" data-id="element-2027" />
          </div>
          <h2 className="text-[18px] font-semibold text-text-primary mb-2" data-id="element-2028">
            Sign out of Aurora Care?
          </h2>
          <p className="text-[15px] text-text-secondary mb-8" data-id="element-2029">
            You will be signed out of your account on this device.
          </p>

          <button onClick={handleLogout} disabled={isLoggingOut} className="w-full h-[52px] bg-danger text-white rounded-[14px] font-medium text-[16px] flex items-center justify-center transition-opacity disabled:opacity-70 mb-3" data-id="element-2030">
            {isLoggingOut ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2031" /> : 'Sign Out'}
          </button>
          <button onClick={() => setShowLogoutSheet(false)} disabled={isLoggingOut} className="w-full h-[52px] bg-transparent text-text-secondary rounded-[14px] font-medium text-[16px]" data-id="element-2032">
            Stay Signed In
          </button>
        </div>
      </BottomSheet>

      {/* Delete Account Bottom Sheet */}
      <BottomSheet isOpen={showDeleteSheet} onClose={() => !isLoggingOut && setShowDeleteSheet(false)} data-id="element-2033">
        <div className="text-center py-2" data-id="element-2034">
          <AlertTriangle size={40} className="text-danger mx-auto mb-4" data-id="element-2035" />
          <h2 className="text-[18px] font-semibold text-danger mb-2" data-id="element-2036">
            Delete your account?
          </h2>
          <p className="text-[15px] text-text-secondary mb-6" data-id="element-2037">
            This will permanently delete your account, all your monitoring
            sessions, and all your data. This cannot be undone.
          </p>

          {deleteError && <div className="bg-danger/10 border border-danger/20 rounded-[12px] p-3 mb-4 text-[14px] text-danger font-medium text-center animate-slideDown" data-id="element-2038">
              {deleteError}
            </div>}

          <div className="text-left mb-8" data-id="element-2039">
            <label htmlFor="deleteConfirm" className="block text-[14px] font-medium text-text-primary mb-2" data-id="element-2040">
              Type DELETE to confirm
            </label>
            <input id="deleteConfirm" type="text" value={deleteInput} onChange={e => setDeleteInput(e.target.value)} placeholder="Type DELETE here" className="w-full h-[52px] rounded-[12px] border border-border px-4 focus:border-danger focus:outline-none transition-colors" data-id="element-2041" />
          </div>

          <button onClick={handleDeleteAccount} disabled={deleteInput !== 'DELETE' || isLoggingOut} className={`w-full h-[52px] rounded-[14px] font-medium text-[16px] flex items-center justify-center transition-all mb-3 ${deleteInput === 'DELETE' ? 'bg-danger text-white' : 'bg-border text-text-secondary opacity-50 cursor-not-allowed'}`} data-id="element-2042">
            {isLoggingOut ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2043" /> : 'Delete Account'}
          </button>
          <button onClick={() => {
          setShowDeleteSheet(false);
          setDeleteInput('');
        }} disabled={isLoggingOut} className="w-full h-[52px] bg-transparent text-text-secondary rounded-[14px] font-medium text-[16px]" data-id="element-2044">
            Cancel
          </button>
        </div>
      </BottomSheet>
    </main>;
}
// Helper Components
function MenuRow({
  icon,
  iconBg,
  title,
  subtitle,
  rightElement,
  onClick,
  hideChevron = false,
  isActive = false
}: {
  icon: React.ReactNode;
  iconBg: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  rightElement?: React.ReactNode;
  onClick?: () => void;
  hideChevron?: boolean;
  isActive?: boolean;
}) {
  return <div onClick={onClick} className={`p-4 flex items-center gap-4 transition-colors ${onClick ? 'cursor-pointer hover:bg-secondary/50' : ''} ${isActive ? 'bg-primary/5 border-l-4 border-primary' : 'border-l-4 border-transparent'}`} data-id="element-2045">
      <div className={`w-[36px] h-[36px] rounded-full flex items-center justify-center flex-shrink-0 ${iconBg}`} data-id="element-2046">
        {icon}
      </div>
      <div className="flex-1 min-w-0" data-id="element-2047">
        <div className="text-[15px] font-medium text-text-primary" data-id="element-2048">{title}</div>
        {subtitle && <div className="text-[13px] text-text-secondary mt-0.5" data-id="element-2049">
            {subtitle}
          </div>}
      </div>
      {rightElement ? rightElement : !hideChevron && onClick ? <ChevronRight size={18} className="text-text-secondary flex-shrink-0" data-id="element-2050" /> : null}
    </div>;
}
function BottomSheet({
  isOpen,
  onClose,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-2051">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/40 z-[100]" data-id="element-2052" />
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[24px] p-6 z-[101] pb-[env(safe-area-inset-bottom)] md:max-w-[480px] md:mx-auto md:rounded-[24px] md:bottom-6 md:left-6 md:right-6" data-id="element-2053">
            <div className="w-[40px] h-[4px] bg-border rounded-full mx-auto mb-6 md:hidden" data-id="element-2054" />
            {children}
          </motion.div>
        </>}
    </AnimatePresence>;
}